import "./App.css";
import content from "./content.json";

function ExternalLink({ href, children, className = "button button--secondary" }) {
  const isExternal = href.startsWith("http");
  return (
    <a className={className} href={href} target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}>
      {children}
    </a>
  );
}

function TagList({ items, label }) {
  return (
    <div className="tag-list" aria-label={label}>
      {items.map((item) => <span className="tag" key={item}>{item}</span>)}
    </div>
  );
}

function App() {
  const currentYear = new Date().getFullYear();

  return (
    <div className="site">
      <a className="skip-link" href="#main">Skip to content</a>

      <header className="site-header">
        <div className="wrap header-row">
          <a className="site-name" href="#top" aria-label={`${content.name}, home`}>DA</a>
          <nav className="site-nav" aria-label="Primary navigation">
            <a href="#work">Work</a>
            <a href="#experience">Experience</a>
            <a href="#skills">Skills</a>
            <a href="#contact">Contact</a>
            <a className="nav-feature" href="/minecraft/">Minecraft survey</a>
            <a className="nav-feature nav-download" href="/downloads/Survival_Safeguards_1.1.0.mcpack?v=4" download="Survival_Safeguards_1.1.0.mcpack">Download pack v1.1</a>
          </nav>
        </div>
      </header>

      <main id="main">
        <section className="hero" id="top">
          <div className="wrap hero-grid">
            <div className="hero-copy">
              <p className="eyebrow">{content.title}</p>
              <h1>{content.name}</h1>
              <p className="hero-headline">{content.headline}</p>
              <p className="hero-summary">{content.summary}</p>
              <div className="button-row">
                <ExternalLink href={content.links.resume} className="button button--primary">View resume</ExternalLink>
                <ExternalLink href={content.links.email}>Email me</ExternalLink>
                <ExternalLink href={content.links.linkedin}>LinkedIn</ExternalLink>
                <a className="button button--minecraft" href="/downloads/Survival_Safeguards_1.1.0.mcpack?v=4" download="Survival_Safeguards_1.1.0.mcpack">Download Minecraft pack v1.1</a>
              </div>
              <div className="status-line"><span aria-hidden="true"></span>{content.status}</div>
            </div>

            <aside className="profile-card">
              <img className="profile-image" src={content.portrait.src} alt={content.portrait.alt}
                width="440" height="520" />
              <div className="profile-card__body">
                <p>{content.location}</p>
                <strong>{content.education.degree}</strong>
                <span>{content.education.school}, {content.education.graduated}</span>
              </div>
            </aside>
          </div>
        </section>

        <section className="proof-strip" aria-label="Selected results">
          <div className="wrap proof-grid">
            {content.proof_points.map((point) => (
              <article key={point.label}><strong>{point.value}</strong><span>{point.label}</span></article>
            ))}
          </div>
        </section>

        <section className="section" id="about">
          <div className="wrap split-section">
            <div className="section-heading">
              <p className="eyebrow">About</p>
              <h2>Practical engineering for real workflows.</h2>
            </div>
            <div className="long-copy">
              {content.about.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            </div>
          </div>
        </section>

        <section className="section section--tinted" id="work">
          <div className="wrap">
            <div className="section-heading section-heading--wide">
              <p className="eyebrow">Selected work</p>
              <h2>Systems built around trust, speed, and clarity.</h2>
            </div>
            <div className="project-list">
              {content.featured_projects.map((project, index) => (
                <article className="project-card" key={project.name}>
                  <div className="project-number">0{index + 1}</div>
                  <div className="project-main">
                    <p className="card-kicker">{project.context}</p>
                    <h3>{project.name}</h3>
                    <p className="project-tagline">{project.tagline}</p>
                    <p>{project.summary}</p>
                    <p className="project-role">{project.role}</p>
                    <TagList items={project.tags} label={`${project.name} technologies`} />
                    {project.links.length > 0 && (
                      <div className="project-links">
                        {project.links.map((link) => (
                          <ExternalLink key={link.href} href={link.href} className="text-link">
                            {link.label} <span aria-hidden="true">↗</span>
                          </ExternalLink>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="project-impact">
                    <p className="card-kicker">Impact</p>
                    <ul>{project.impact.map((item) => <li key={item}>{item}</li>)}</ul>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section" id="experience">
          <div className="wrap experience-layout">
            <div>
              <div className="section-heading">
                <p className="eyebrow">Experience</p><h2>Work and teaching</h2>
              </div>
              <div className="experience-list">
                {content.experience.map((item) => (
                  <article key={`${item.role}-${item.org}`}>
                    <p className="card-kicker">{item.org}</p><h3>{item.role}</h3>
                    <ul>{item.details.map((detail) => <li key={detail}>{detail}</li>)}</ul>
                  </article>
                ))}
              </div>
            </div>
            <aside className="education-card">
              <p className="eyebrow">Education</p><h3>{content.education.degree}</h3>
              <p>{content.education.school}</p><strong>{content.education.graduated}</strong>
            </aside>
          </div>
        </section>

        <section className="section section--dark" id="skills">
          <div className="wrap">
            <div className="section-heading section-heading--wide">
              <p className="eyebrow">Technical toolkit</p><h2>Tools I use to ship dependable software.</h2>
            </div>
            <div className="skills-grid">
              {content.skill_groups.map((group) => (
                <article key={group.title}><h3>{group.title}</h3>
                  <TagList items={group.items} label={group.title} /></article>
              ))}
            </div>
          </div>
        </section>

        <section className="section" id="contact">
          <div className="wrap contact-card">
            <div><p className="eyebrow">Contact</p><h2>Let&apos;s build something useful.</h2>
              <p>{content.contact_note}</p></div>
            <div className="button-row">
              <ExternalLink href={content.links.email} className="button button--primary">Email me</ExternalLink>
              <ExternalLink href={content.links.github}>GitHub</ExternalLink>
              <ExternalLink href={content.links.linkedin}>LinkedIn</ExternalLink>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="wrap footer-row"><p>{content.name} · {content.title}</p><p>© {currentYear}</p></div>
      </footer>
    </div>
  );
}

export default App;
